/*!
 * perf-debug.js — Performance API instrumentation detector.
 *
 * Background:
 *   On rastah.co, `performance.getEntriesByType('paint')` and
 *   `getEntriesByType('largest-contentful-paint')` return ZERO entries
 *   when queried after page load. This blocks empirical Web Vitals
 *   measurement (FCP, LCP) and prevents proper before/after speed
 *   comparison for our perf work.
 *
 *   Theme-side `grep` for `performance.clear*` returned no matches —
 *   so the clearing (or whatever's preventing entries from being
 *   accessible) is coming from a 3rd-party script loaded into the
 *   page (Bugsnag, Klaviyo, Pixel sandbox, Stiletto's profiler,
 *   Smile.io launcher, etc.).
 *
 *   This script identifies the culprit by:
 *     1. Wrapping `clearMarks`, `clearMeasures`, `clearResourceTimings`
 *        with logging shims that log a stack trace on every call.
 *     2. Setting up early `PerformanceObserver`s for `paint`,
 *        `largest-contentful-paint`, and `longtask` to verify whether
 *        the entries fire at all (vs. fire and get cleared).
 *     3. Dumping a 5-second snapshot to console for at-a-glance triage.
 *
 *   Toggled on with `?debug_perf=1` in the URL — zero impact on normal
 *   visits. Output is console-only; no network or storage side effects.
 *
 *   Once the offender is identified, this asset can be deleted (it's
 *   purely diagnostic).
 */

(function () {
  // Only activate when explicitly opted in via query param.
  // Reading location.search avoids any Liquid templating on theme load —
  // this script is unconditionally injected, but is a no-op without the flag.
  if (!/[?&]debug_perf=1\b/.test(window.location.search)) return;

  console.log(
    '%c[perf-debug] active',
    'color:#fff;background:#222;padding:2px 6px;border-radius:3px',
    '— wrapping performance.clear* and observing paint/lcp/longtask. Add ?debug_perf=1 to any URL.'
  );

  // ----- 1. Wrap clear methods with stack-tracing shims -----
  ['clearMarks', 'clearMeasures', 'clearResourceTimings'].forEach(function (method) {
    if (typeof performance[method] !== 'function') return;
    var orig = performance[method].bind(performance);
    performance[method] = function () {
      console.warn(
        '[perf-debug] performance.' + method + '() called',
        '\n  args: ' + JSON.stringify(Array.from(arguments)),
        '\n  stack: ' + (new Error('stack').stack || '(unavailable)')
      );
      return orig.apply(performance, arguments);
    };
  });

  // ----- 2. Early observers for paint, lcp, longtask -----
  if (typeof PerformanceObserver === 'undefined') {
    console.warn('[perf-debug] PerformanceObserver not available — observation skipped');
    return;
  }

  var captures = { paint: [], lcp: [], longtask: [] };

  function observe(type, key) {
    try {
      var po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          captures[key].push({
            entryType: e.entryType,
            name: e.name || '(anon)',
            startTime: Math.round(e.startTime),
            duration: Math.round(e.duration || 0),
            // Only relevant for LCP entries:
            element: e.element ? (e.element.tagName + (e.element.id ? '#' + e.element.id : '')) : null
          });
          console.log('[perf-debug] ' + e.entryType + ':', e.name || '(anon)', '@', Math.round(e.startTime), 'ms');
        });
      });
      po.observe({ type: type, buffered: true });
    } catch (err) {
      console.warn('[perf-debug] could not observe ' + type + ':', err.message);
    }
  }

  observe('paint', 'paint');
  observe('largest-contentful-paint', 'lcp');
  observe('longtask', 'longtask');
  // Layout-shift contributes to CLS — observe it too so we have raw entries.
  captures.layout_shift = [];
  observe('layout-shift', 'layout_shift');

  window.__perfDebugCaptures = captures;

  // ----- 3. Augment with the official `web-vitals` library -----
  //
  // The `getEntriesByType('paint')` route returned 0 entries on rastah.co
  // even with a buffered PerformanceObserver in place. The official
  // `web-vitals` library uses callback-based metrics with attribution
  // and falls back gracefully when entries fire late or via visibility
  // changes — sidestepping the "queried too early" failure mode and
  // giving values that match Lighthouse / CrUX exactly.
  //
  // Loaded from CDN only when ?debug_perf=1 is active. ~3 KB gzipped.
  // Comparison data lands in `captures.web_vitals` for the snapshot dumps.
  captures.web_vitals = {};

  (function loadWebVitals() {
    var s = document.createElement('script');
    s.src = 'https://unpkg.com/web-vitals@4/dist/web-vitals.iife.js';
    s.crossOrigin = 'anonymous';
    s.onload = function () {
      try {
        var wv = window.webVitals;
        if (!wv) {
          console.warn('[perf-debug] web-vitals loaded but webVitals global missing');
          return;
        }
        ['onCLS', 'onFCP', 'onLCP', 'onINP', 'onTTFB'].forEach(function (fn) {
          if (typeof wv[fn] === 'function') {
            wv[fn](function (metric) {
              captures.web_vitals[metric.name] = {
                value: Math.round(metric.value * 100) / 100,
                rating: metric.rating,
                delta: Math.round(metric.delta * 100) / 100,
                id: metric.id,
                navigationType: metric.navigationType,
                entries_count: (metric.entries || []).length
              };
              console.log(
                '%c[perf-debug] ' + metric.name + ' (web-vitals)',
                'color:#fff;background:#080;padding:2px 6px;border-radius:3px',
                metric.value, metric.rating
              );
            });
          }
        });
        console.log('[perf-debug] web-vitals callbacks registered');
      } catch (err) {
        console.warn('[perf-debug] web-vitals init error:', err);
      }
    };
    s.onerror = function () {
      console.warn('[perf-debug] web-vitals failed to load from CDN');
    };
    document.head.appendChild(s);
  })();

  // ----- 4. Periodic snapshot dump for triage -----
  function dumpSnapshot(label) {
    var snapshot = {
      label: label,
      via_observer: {
        paint: captures.paint.length,
        lcp: captures.lcp.length,
        longtask: captures.longtask.length,
        layout_shift: captures.layout_shift ? captures.layout_shift.length : 0
      },
      via_getEntriesByType: {
        paint: performance.getEntriesByType('paint').length,
        lcp: (performance.getEntriesByType('largest-contentful-paint') || []).length,
        longtask: (performance.getEntriesByType('longtask') || []).length
      },
      via_web_vitals: captures.web_vitals,
      paint_entries: captures.paint,
      lcp_entries: captures.lcp.map(function (e) { return { startTime: e.startTime, name: e.name, element: e.element }; })
    };
    console.log('%c[perf-debug] snapshot @ ' + label, 'color:#fff;background:#066;padding:2px 6px;border-radius:3px', snapshot);
  }

  setTimeout(function () { dumpSnapshot('5s'); }, 5000);
  setTimeout(function () { dumpSnapshot('15s'); }, 15000);

  // Expose manual dump for live debugging
  window.__perfDebugDump = function () { dumpSnapshot('manual'); };
})();
